import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { omit } from 'lodash';

import { ELayoutManagerMode, EWidgetRegion } from '#shared';

import {
  useSelectRegionSectionOrder,
  useSelectRegionTemplateColumns,
} from '../../slices';

import { Section, type SectionProps } from './Section';

const PREFIX = 'TSMED-LayoutManager-Region';

export const regionClasses = {
  root: `${PREFIX}-root`,
  label: `${PREFIX}-label`,
  body: `${PREFIX}-body`,
};

const Root = styled(Box, {
  shouldForwardProp: prop => !['styleSlots'].includes(prop as string),
})<{
  styleSlots?: RegionProps['styleSlots'];
}>(({ theme, styleSlots }) => ({
  [`&.${regionClasses.root}`]: {
    position: 'relative',
    ...styleSlots?.region?.root,
  },
  [`.${regionClasses.body}`]: {
    display: 'grid',
    gap: theme.spacing(1),
    alignItems: 'stretch',
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    container: 'region-content / size',
    ...styleSlots?.region?.body,
  },
}));

export interface RegionProps {
  region: EWidgetRegion;
  area: string;
  mode: ELayoutManagerMode;
  styleSlots?: {
    region?: Partial<Record<keyof typeof regionClasses, React.CSSProperties>>;
  } & SectionProps['styleSlots'];
}

export const Region = ({ region, area, mode, styleSlots }: RegionProps) => {
  const sectionIds = useSelectRegionSectionOrder(region);
  const columns = useSelectRegionTemplateColumns(region);

  const sectionStyleSlots = omit(styleSlots, 'region');

  return (
    <Root
      className={regionClasses.root}
      gridArea={area}
      styleSlots={styleSlots}
    >
      <Box className={regionClasses.body} gridTemplateColumns={columns}>
        {sectionIds.map(sectionId => (
          <Section
            key={sectionId}
            sectionId={sectionId}
            mode={mode}
            styleSlots={sectionStyleSlots}
          />
        ))}
      </Box>
    </Root>
  );
};
